import { useState } from "react";
import { message } from "antd";
import { getProductsNearbyStockService, generateTransferRequestService } from "../../../services";
import useAuthStore from "../../../stores/useAuthStore";

const buildTransferRequestMutation = ({ fromBunitId, toBunitId, productId, qty, userId }) => {
  // current date & time
  const now = new Date();

  // YYYY-MM-DD
  const requiredDate = now.toISOString().split("T")[0];

  // YYYY-MM-DD HH:mm:ss.SSS
  const currentTimestamp = now.toISOString().replace("T", " ").replace("Z", "");

  const dataLoad = [
    {
      fromBunitId,
      toBunitId,
      created: currentTimestamp,
      createdby: userId,
      updated: currentTimestamp,
      updatedby: userId,
      requiredDate,
      requestFor: "ST",
      description: "",
      lines: [
        {
          productId,
          qtyRequested: qty,
          pRequisitionLineId: null,
        },
      ],
    },
  ];

  const dataLoadString = JSON.stringify(dataLoad).replace(/"/g, '\\"');

  return `
    mutation {
      generateTransferRequest(
        dataLoad: "${dataLoadString}"
      ) {
        message
        code
        recordId
      }
    }
  `;
};

const useStockTransfer = ({ tillData }) => {
  const [stockDrawerVisible, setStockDrawerVisible] = useState(false);
  const [selectedStockProduct, setSelectedStockProduct] = useState(null);
  const [stockLoading, setStockLoading] = useState(false);
  const [stockList, setStockList] = useState([]);
  const [selectedStockRow, setSelectedStockRow] = useState(null);
  const [requestQty, setRequestQty] = useState(1);
  const [selectedOutlet, setSelectedOutlet] = useState(null);

  const currentWarehouseId = tillData?.tillAccess?.csBunit?.mWarehouse?.mWarehouseID;

  const filteredStockList = stockList?.filter((store) => {
    const warehouseId = store?.warehouse?.mWarehouseID;
    return warehouseId && warehouseId !== currentWarehouseId;
  });

  const getProductNearByStock = async (product) => {
    setStockLoading(true);
    try {
      const stockInputValues = {
        query: `
        query NearbyStock($bUnitId: String, $code: String) {
          getProductsNearbyStock(
            bUnitId: $bUnitId,
            code: $code
          ) {
            csBunitId
            value
            name
            totalQty
            stores {
              csNearbyStoreId
              name
              nearbybUnitId
              viewStock
              isIssue
              warehouse {
                mWarehouseID
                name
                value
                storageBin {
                  mStoragebinID
                  name
                  stocks {
                    mStockID
                    qtyonhand
                    product {
                      mProductId
                      value
                      name
                      upc
                      uom {
                        csUomId
                        name
                      }
                      productCategory {
                        mProductCategoryId
                        value
                        name
                      }
                    }
                    batch {
                      mBatchId
                      batchno
                      upc
                      price
                      startdate
                      enddate
                      life
                      purchasePrice
                      listPrice
                      purchaseDate
                    }
                  }
                  totalQty
                }
              }
            }
          }
        }
      `,
        variables: { bUnitId: tillData.tillAccess.csBunit.csBunitId, code: product.value },
      };

      const response = await getProductsNearbyStockService(stockInputValues);

      const { stores, totalQty, csBunitId } = response.data.data.getProductsNearbyStock;

      const currentStock = {
        csNearbyStoreId: "53F4FBEBDAB94C4AB205B7415EBD739E",
        name: tillData.tillAccess.csBunit.mWarehouse.name,
        nearbybUnitId: "EEF9FADCE0D74AE3B8B6096E6C35B6AA",
        viewStock: "Y",
        isIssue: "Y",
        warehouse: {
          mWarehouseID: tillData.tillAccess.csBunit.mWarehouse.mWarehouseID,
          name: tillData.tillAccess.csBunit.mWarehouse.name,
          value: "Central WH TTLLC",
          storageBin: {
            mStoragebinID: "09581CA09C154830A20B88370C501FD6",
            name: "Central Warehouse",
            stocks: [],
            totalQty: totalQty || 0,
          },
        },
      };

      const isStorePresent = stores?.some((store) => store?.nearbybUnitId === csBunitId);
      setStockList(isStorePresent ? stores : [currentStock, ...stores]);
    } catch (error) {
      console.error("Error fetching nearby stock:", error);
      message.error("Failed to fetch stock data");
    } finally {
      setStockLoading(false);
    }
  };

  const handleViewStockClick = (product) => {
    setSelectedStockProduct(product);
    setStockDrawerVisible(true);
    getProductNearByStock(product);
  };

  const resetRequestFields = () => {
    setSelectedOutlet(null);
    setSelectedStockRow(null);
    setRequestQty("");
  };

  const handleStockTransferRequest = async () => {
    if (!selectedStockRow) {
      return message.warning("Please select outlet");
    }

    if (!requestQty || requestQty < 1) {
      return message.warning("Enter valid quantity");
    }

    const totalQty = Number(selectedStockRow?.qty || 0);

    if (requestQty > totalQty) {
      return message.warning(`Only ${totalQty} available in selected outlet`);
    }

    const tillData = useAuthStore.getState().tillData;

    const mutation = buildTransferRequestMutation({
      fromBunitId: selectedStockRow?.nearbybUnitId,
      toBunitId: tillData.tillAccess.csBunit.csBunitId,
      productId: selectedStockProduct?.mProductId,
      qty: requestQty,
      userId: tillData.tillAccess.csUserId,
    });

    try {
      const response = await generateTransferRequestService({ query: mutation });
      const result = response?.data?.data?.generateTransferRequest;

      if (result?.code === "200") {
        message.success(result.message);
        resetRequestFields();
        setStockDrawerVisible(false);
      } else {
        message.error(result?.message || "Transfer request failed");
      }
    } catch (err) {
      console.error(err);
      message.error("Something went wrong");
    }
  };

  // Build transformed rows from stockList
  const transformedRows = [];

  stockList?.forEach((store) => {
    const warehouse = store?.warehouse;
    const stocks = warehouse?.storageBin?.stocks || [];

    stocks.forEach((stock) => {
      const qty = Number(stock?.qtyonhand || 0);
      if (qty <= 0) return;

      const isCurrentStore = warehouse?.mWarehouseID === currentWarehouseId;

      transformedRows.push({
        key: `${warehouse?.mWarehouseID}_${stock?.batch?.mBatchId}`,
        nearbybUnitId: store?.nearbybUnitId,
        warehouseId: warehouse?.mWarehouseID,
        batchId: stock?.batch?.mBatchId,
        outletName: store?.name,
        batchNumber: stock?.batch?.batchno || "-",
        qty,
        unit: stock?.product?.uom?.name || "pcs",
        isCurrentStore,

        // FROM PRODUCT ONLY
        mrp: selectedStockProduct?.priceList?.[0]?.pricestd ?? selectedStockProduct?.sunitprice ?? selectedStockProduct?.slistprice ?? 0,

        batchRate: stock?.batch?.listPrice || 0,
      });
    });
  });

  const outletOptions = transformedRows.map((row) => ({
    value: row.key,
    label: `${row.outletName} - ${row.batchNumber}`,
  }));

  const closeStockDrawer = () => {
    setStockDrawerVisible(false);
    setStockList([]);
    setSelectedStockProduct(null);
    setSelectedStockRow(null);
    setRequestQty(1);
    resetRequestFields();
  };

  return {
    stockDrawerVisible,
    setStockDrawerVisible,
    selectedStockProduct,
    setSelectedStockProduct,
    stockLoading,
    stockList,
    setStockList,
    selectedStockRow,
    setSelectedStockRow,
    requestQty,
    setRequestQty,
    selectedOutlet,
    setSelectedOutlet,
    currentWarehouseId,
    filteredStockList,
    handleViewStockClick,
    handleStockTransferRequest,
    resetRequestFields,
    transformedRows,
    outletOptions,
    closeStockDrawer,
  };
};

export default useStockTransfer;
